import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, DatePicker, Row, Table } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import OrderService from "services/order";
import { ExportFile } from "hooks/FileHandler";
import ModalProgress from "components/ModalProgress/ModalProgress";
import BasicTablePaging from "components/BasicTablePaging/BasicTablePaging"; // chỉnh path đúng dự án
import { LAYOUT } from "constants/layout"; // chỉnh path đúng dự án
import { PriceText } from "components/DataTableText";
import addKeyLocalStorage from "helper/localStorage";
import "Page/Receipt/receipt.scss";
import AffiliateOrderStatsWidgets from "components/AffiliateWidget";
import ButtonExportExcel from "components/ButtonExportExcel";

const { RangePicker } = DatePicker;
const PAGE_SIZE = 20;

const AFFILIATE_SUMMARY_EXPORT_FIELDS = [
  { key: "index", labelKey: "affiliate.summary.index" },
  { key: "staffName", labelKey: "affiliate.summary.staffName" },
  { key: "totalNewOrders", labelKey: "affiliate.summary.totalNewOrders" },
  { key: "totalSuccessfulOrders", labelKey: "affiliate.summary.totalSuccessfulOrders" },
  { key: "totalCommission", labelKey: "affiliate.summary.totalCommission" },
  { key: "totalAvenue", labelKey: "affiliate.summary.totalAvenue" },
];

const AffiliateSummary = () => {
  const { t: translate } = useTranslation();
  const member = useSelector((state) => state.member || {});
  const { onExportExcel } = ExportFile();

  // ----- Filter / pagination state (nguồn dữ liệu duy nhất) -----
  const [dataFilter, setDataFilter] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
    skip: 0,
    limit: PAGE_SIZE,
  });

  // ----- Table data state -----
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ----- Stats widget state -----
  const [affiliateSummaryStats, setAffiliateSummaryStats] = useState({});
  const [isAffiliateStatsLoading, setIsAffiliateStatsLoading] = useState(false);

  // ----- Export state -----
  const [isExportProgress, setIsExportProgress] = useState(false);
  const [exportPercent, setExportPercent] = useState(0);

  // ----- Fetch table data -----
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OrderService.getSummaryListOrderAffiliate({
        startDate: moment(dataFilter.startDate).startOf("day"),
        endDate: moment(dataFilter.endDate).endOf("day"),
        skip: dataFilter.skip,
        limit: dataFilter.limit,
      });
      setData(response?.data || []);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dataFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleChangePage = (pageNum) => {
    setDataFilter((prev) => ({
      ...prev,
      skip: (pageNum - 1) * prev.limit,
    }));
  };

  // ----- Fetch widget stats (kỳ hiện tại + kỳ trước 1 tháng) -----
  const loadAffiliateSummaryStats = useCallback(async () => {
    setIsAffiliateStatsLoading(true);
    try {
      const currentParams = {
        startDate: moment(dataFilter.startDate).startOf("day"),
        endDate: moment(dataFilter.endDate).endOf("day"),
      };
      const previousParams = {
        startDate: moment(dataFilter.startDate).subtract(1, "month").startOf("day"),
        endDate: moment(dataFilter.endDate).subtract(1, "month").endOf("day"),
      };

      const [currentRes, previousRes] = await Promise.all([
        OrderService.getAffiliateOrderStatistics(currentParams),
        OrderService.getAffiliateOrderStatistics(previousParams),
      ]);
      setAffiliateSummaryStats({
        current: currentRes || {},
        previous: previousRes || {},
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsAffiliateStatsLoading(false);
    }
  }, [dataFilter.startDate, dataFilter.endDate]);

  useEffect(() => {
    loadAffiliateSummaryStats();
  }, [loadAffiliateSummaryStats]);

  // ----- Reload cả bảng lẫn widget -----
  const handleReload = () => {
    fetchSummary();
    loadAffiliateSummaryStats();
  };

  // ----- Table columns -----
  const columns = [
    {
      title: translate("affiliate.summary.index"),
      dataIndex: "referUserId",
      key: "referUserId",
      width: LAYOUT.columns.small,
      align: "center",
      render: (_, __, index) => <span>{dataFilter.skip + index + 1}</span>,
    },
    {
      title: translate("affiliate.summary.staffName"),
      dataIndex: "staffName",
      key: "staffName",
      width: LAYOUT.columns.medium,
      align: "center",
      ellipsis: true,
      render: (staffName) => <span>{staffName}</span>,
    },
    {
      title: translate("affiliate.summary.totalNewOrders"),
      dataIndex: "totalNewOrders",
      key: "totalNewOrders",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (totalNewOrders) => <span>{totalNewOrders || 0}</span>,
    },
    {
      title: translate("affiliate.summary.totalSuccessfulOrders"),
      dataIndex: "totalSuccessfulOrders",
      key: "totalSuccessfulOrders",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (totalSuccessfulOrders) => <span>{totalSuccessfulOrders || 0}</span>,
    },
    {
      title: translate("affiliate.summary.totalAvenue"),
      dataIndex: "totalAvenue",
      key: "totalAvenue",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (totalAvenue) => (
        <PriceText className="order-amount" price={totalAvenue || 0} />
      ),
    },
    {
      title: translate("affiliate.summary.totalCommission"),
      dataIndex: "totalCommission",
      key: "totalCommission",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (totalCommission) => (
        <PriceText className="order-amount" price={totalCommission || 0} />
      ),
    },
  ];


  const fetchAffiliateSummary = ({ skip, limit }) =>
    OrderService.getSummaryListOrderAffiliate({
      startDate: moment(dataFilter.startDate).startOf("day"),
      endDate: moment(dataFilter.endDate).endOf("day"),
      skip,
      limit,
  });

  const getAffiliateFileName = () => {
    const memberInfo = JSON.parse(window.localStorage.getItem(addKeyLocalStorage("data"))) || {};
    const stationCode = memberInfo.stationCode || memberInfo.stationsId || "ALL";
    const exportTime = moment().format("YYYYMMDD_HHmmss");
    return `Tiếp thị liên kết - Tổng kết ${stationCode}_${exportTime}.xlsx`;
  };

  return (
    <div>
       <AffiliateOrderStatsWidgets affiliateOrderStats={affiliateSummaryStats} loading={isAffiliateStatsLoading} />

      <Row gutter={[24, 24]} align="middle" className="mb-3">
        <Col xs={24} sm={16} md={12} lg={8} xl={4}>
          <RangePicker
            className="w-100"
            format="DD/MM/YYYY"
            allowClear={false}
            value={[
              dataFilter.startDate ? moment(dataFilter.startDate) : null,
              dataFilter.endDate ? moment(dataFilter.endDate) : null,
            ]}
            onChange={(dates) => {
              if (!dates) return;
              setDataFilter((prev) => ({
                ...prev,
                startDate: dates[0] ? dates[0].toDate() : prev.startDate,
                endDate: dates[1] ? dates[1].toDate() : prev.endDate,
                skip: 0,
              }));
            }}
          />
        </Col>
        <Col xl="auto" lg={6} md={8} sm={12} xs={24} className="d-flex flex-wrap gap-4">
          <ButtonExportExcel
            text={translate("affiliate.actions.export")}
            fetchApi={fetchAffiliateSummary}
            fields={AFFILIATE_SUMMARY_EXPORT_FIELDS}
            getFileName={getAffiliateFileName}
            translate={translate}
          />
          <Button
            type="default"
            icon={<ReloadOutlined />}
            loading={loading}
            disabled={loading}
            onClick={handleReload}
          />
        </Col>
      </Row>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="referUserId"
        loading={loading}
        scroll={{ x: 1312 }}
        pagination={false}
      />

      <BasicTablePaging
        handlePaginations={handleChangePage}
        count={data.length < dataFilter.limit}
        currentPage={dataFilter.skip / dataFilter.limit + 1}
      />

      <ModalProgress
        visible={isExportProgress}
        setVisible={setIsExportProgress}
        percent={exportPercent}
      />
    </div>
  );
};

export default AffiliateSummary;